import React, { useState } from 'react';
import { forcePasswordReset } from '../../api/admin';

const PasswordResetManager: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [recentResets, setRecentResets] = useState<Array<{ email: string; timestamp: string }>>([]);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter an email address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setTempPassword('');

    try {
      const response = await forcePasswordReset(email);

      if (response.temporary_password) {
        // Email failed, show temp password
        setTempPassword(response.temporary_password);
        setError(response.email_error || 'Email failed to send. Please provide the temporary password to the user manually.');
      } else {
        // Success
        setSuccess(`Password reset email sent successfully to ${email}`);
        // Add to recent resets
        setRecentResets(prev => [
          { email, timestamp: new Date().toLocaleString() },
          ...prev.slice(0, 9) // Keep only last 10
        ]);
        setEmail(''); // Clear the input
      }
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-4 text-zinc-100">Force Password Reset</h2>

        {/* Reset Form */}
        <div className="card-dark rounded-xl p-6 mb-6">
          <form onSubmit={handleReset} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-zinc-300 mb-2">
                User Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                className="input-dark w-full px-4 py-2 rounded-lg"
                disabled={loading}
              />
            </div>

            {error && (
              <div className="alert alert-error">
                {error}
              </div>
            )}

            {success && (
              <div className="alert alert-success">
                {success}
              </div>
            )}

            {tempPassword && (
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                <div className="text-amber-400">
                  <p className="font-semibold mb-2">Temporary Password (Email Failed):</p>
                  <div className="bg-zinc-800 border border-amber-500/30 rounded-lg p-3 font-mono text-lg select-all text-zinc-100">
                    {tempPassword}
                  </div>
                  <p className="text-sm mt-2 text-amber-400/80">
                    Please provide this temporary password to the user manually. They can use it to log in and should change it immediately.
                  </p>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Resetting...' : 'Force Password Reset'}
            </button>
          </form>
        </div>

        {/* Instructions */}
        <div className="alert alert-info mb-6">
          <h3 className="font-semibold mb-2">How it works:</h3>
          <ul className="list-disc list-inside space-y-1 text-sm opacity-90">
            <li>Enter the user's email address and click "Force Password Reset"</li>
            <li>The system will generate a temporary password and attempt to email it to the user</li>
            <li>If email delivery fails, the temporary password will be displayed here for you to provide manually</li>
            <li>The user's existing password will be replaced immediately</li>
            <li>All active sessions for the user will be invalidated</li>
            <li>The user must use the temporary password to log in and should change it immediately</li>
          </ul>
        </div>

        {/* Recent Resets */}
        {recentResets.length > 0 && (
          <div className="card-dark rounded-xl overflow-hidden">
            <div className="px-6 py-3 bg-zinc-800/50 border-b border-zinc-700/50">
              <h3 className="text-sm font-medium text-zinc-300">Recent Password Resets</h3>
            </div>
            <div className="divide-y divide-zinc-700/50">
              {recentResets.map((reset, index) => (
                <div key={index} className="px-6 py-3 flex justify-between">
                  <span className="text-sm text-zinc-200">{reset.email}</span>
                  <span className="text-sm text-zinc-500">{reset.timestamp}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PasswordResetManager;
